import { cfgTheme, Theme } from '@infinitro/config';
/**
 * Brand-preferred default dial code (as a bare CountryCodeOption value, e.g. '90' for +90).
 * The backend's phone-country-config list has no notion of a "default" country — it's whatever
 * dial codes are returned, sorted numerically low-to-high — so this is the only place a brand
 * can override that with an explicit preference. Keyed on the stable `Theme` enum rather than
 * `cfgBrand()`, since the latter returns an environment-specific domain (e.g. `turboslot.com` in
 * prod vs `turbo-gamma.previewenv.net` in preview) that would silently fail to match outside prod.
 */
const THEME_DEFAULT_DIAL_CODE = {
    [Theme.Turboslots]: '90',
    [Theme.Luna]: '90'
};
export function normalizeNationalNumber(value, maxLength = 15) {
    return value.replace(/\D/g, '').slice(0, maxLength);
}
/**
 * National-number variants to try against e164Regex.
 * Users often type a trunk prefix (`0`, Hungary `06`) or paste the country code.
 */
export function nationalNumberCandidates(telCountryCode, nationalNumber) {
    const raw = normalizeNationalNumber(nationalNumber);
    const code = String(telCountryCode).replace(/\D/g, '');
    const candidates = [];
    const seen = new Set();
    const add = (digits) => {
        if (!digits || seen.has(digits))
            return;
        seen.add(digits);
        candidates.push(digits);
    };
    add(raw);
    let stripped = raw;
    if (stripped.startsWith('00')) {
        stripped = stripped.slice(2);
        add(stripped);
    }
    if (code && stripped.startsWith(code)) {
        stripped = stripped.slice(code.length);
        add(stripped);
    }
    // Common trunk prefixes: single `0`, or Hungary-style `06`
    if (stripped.startsWith('06')) {
        add(stripped.slice(2));
    }
    if (stripped.startsWith('0')) {
        add(stripped.slice(1));
    }
    if (raw.startsWith('06')) {
        add(raw.slice(2));
    }
    if (raw.startsWith('0')) {
        add(raw.slice(1));
    }
    return candidates;
}
/**
 * National digits for API / E.164 build — prefer first stripped variant over raw
 * and over later partial strips (e.g. Hungary `06…` → `301234567`, not `6301234567`).
 */
export function nationalSignificantDigits(telCountryCode, nationalNumber) {
    const candidates = nationalNumberCandidates(telCountryCode, nationalNumber);
    if (candidates.length === 0)
        return '';
    if (candidates.length === 1)
        return candidates[0];
    return candidates[1];
}
export function buildE164Candidate(telCountryCode, nationalNumber) {
    const code = String(telCountryCode).replace(/\D/g, '');
    const digits = nationalSignificantDigits(telCountryCode, nationalNumber);
    return `+${code}${digits}`;
}
export function compileE164Regex(pattern) {
    const trimmed = pattern?.trim();
    // Empty patterns compile but match everything — treat as invalid config.
    if (!trimmed)
        return null;
    try {
        return new RegExp(trimmed);
    }
    catch {
        return null;
    }
}
/** Dropdown value is the dial code only — shared dial codes (+1, +44, +7) collapse to one option. */
export function optionValueForConfig(row) {
    return String(row.telCountryCode);
}
export function parseOptionValue(value) {
    const telCountryCode = value.replace(/\D/g, '');
    if (!telCountryCode)
        return null;
    return { telCountryCode };
}
export function formatCountryOptionLabel(row) {
    return `+${row.telCountryCode}`;
}
/**
 * One dropdown option per distinct dial code, labelled with the dial code only (e.g. `+1`).
 * The matched country/region is resolved from the typed number, not from selection.
 */
export function buildCountryOptions(config) {
    const seen = new Set();
    const options = [];
    for (const row of config) {
        const value = optionValueForConfig(row);
        if (seen.has(value))
            continue;
        seen.add(value);
        options.push({ value, label: formatCountryOptionLabel(row) });
    }
    return options.sort((a, b) => Number(a.value) - Number(b.value));
}
/** @deprecated Alias of buildCountryOptions — both now produce dial-code-only options. */
export function buildDistinctCountryCodeOptions(config) {
    return buildCountryOptions(config);
}
export function getDefaultCountryOption(config) {
    const options = buildCountryOptions(config);
    const preferred = THEME_DEFAULT_DIAL_CODE[cfgTheme()];
    if (preferred && options.some((option) => option.value === preferred)) {
        return preferred;
    }
    return options[0]?.value ?? '';
}
export function getDefaultCountryCode(config) {
    return getDefaultCountryOption(config);
}
/** First config row matching the dial code (shared codes return the first row). */
export function findConfigByOptionValue(config, optionValue) {
    const parsed = parseOptionValue(optionValue);
    if (!parsed)
        return null;
    return config.find((row) => String(row.telCountryCode) === parsed.telCountryCode) ?? null;
}
export function validatePhoneAgainstConfig(config, telCountryCode, nationalNumber) {
    const codeNum = Number(telCountryCode);
    const fallbackDigits = nationalSignificantDigits(telCountryCode, nationalNumber);
    const fallbackE164 = buildE164Candidate(telCountryCode, nationalNumber);
    const digitCandidates = nationalNumberCandidates(telCountryCode, nationalNumber);
    const noMatch = {
        valid: false,
        e164: fallbackE164,
        nationalDigits: fallbackDigits,
        matchedConfig: null,
        ambiguous: false
    };
    if (!digitCandidates.length || !Number.isFinite(codeNum)) {
        return noMatch;
    }
    const candidates = config.filter((entry) => entry.telCountryCode === codeNum);
    if (candidates.length === 0) {
        return noMatch;
    }
    // Selection carries the dial code only, so the country/region is resolved from the number:
    // test every config sharing the dial code and collect the distinct countries whose regex passes.
    const matchedCountries = new Set();
    let winner = null;
    for (const digits of digitCandidates) {
        const e164 = `+${codeNum}${digits}`;
        for (const row of candidates) {
            const regex = compileE164Regex(row.e164Regex);
            if (regex?.test(e164)) {
                if (!matchedCountries.has(row.country)) {
                    matchedCountries.add(row.country);
                    winner ??= { e164, nationalDigits: digits, config: row };
                }
            }
        }
    }
    if (!winner) {
        return noMatch;
    }
    const ambiguous = matchedCountries.size > 1;
    return {
        valid: true,
        e164: winner.e164,
        nationalDigits: winner.nationalDigits,
        // Only expose a resolved country when it is unambiguous (drives the secondary region hint).
        matchedConfig: ambiguous ? null : winner.config,
        ambiguous
    };
}
export function formatMatchedRegionLabel(config) {
    const regions = config.telRegionCode?.filter((code) => code != null) ?? [];
    if (regions.length === 0) {
        return `+${config.telCountryCode} ${config.countryName}`.trim();
    }
    return `+${config.telCountryCode} ${regions.join(' ')} - ${config.countryName}`;
}
